// Talent Management > Declines: every outreach that ended in a no, across every project.
//
// The per-project view of the same rows already exists (a project's Sellers tab renders
// the reason on the thread); this is the cross-project lane that answers what no single
// project can, which is why talents are turning us down right now, in their own words.
//
// Two columns carry the weight. REASON is the coarse enum; WHY is the free-text detail,
// shown verbatim because this page is behind `talent_management`. The client-facing
// dashboard deliberately never reads that column: HERALD de-attributes it into a "why
// talents passed" line there, and an operator needs the words that line is hiding.
//
// WHO is the third, and it is what stops the page lying. `state='declined'` covers two
// different events: a talent turning US down, and us declining THEM after JUNO judged
// their offer out of scope. Reading the mix as "why talents passed" would credit our own
// decisions to the talents, and `out_of_scope` is exactly the value both produce.
//
// Same lane and same rules as the Offers tab beside it: OPERATIONAL per-record truth from
// Postgres, newest first, keyset "load more". Deliberately NO breakdown, count, or rate by
// reason, here or anywhere on this page. That shape is a metric, metrics come from curated
// BigQuery via the Dashboard tab, and a second definition assembled in the browser would
// disagree with Superset the first time either side changed what counts.
import {
  Badge,
  Button,
  DateRangeFilter,
  EMPTY_DATE_RANGE,
  facetOptions,
  isDateRangeActive,
  SortableTh,
  TableExport,
  TableFilters,
  type DateRange,
  type FilterColumn,
  useColumnFilters,
  useRestoredState,
  useTableSort,
} from "@recruiter/shared";
import type { AdminDeclineRow } from "@recruiter/shared/types";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { AreaBoundary } from "../components/area-gate";
import { ArrowRightIcon } from "../components/icons";
import { SectionTabs, TALENT_TABS } from "../components/section-tabs";
import { EmptyState, TableSkeleton } from "../components/states";
import { ExplorerProjectLink, ExplorerTalentLink } from "../components/explorer-link";
import { DECLINE_REASON_ORDER, declineReasonLabel } from "../lib/decline-reasons";
import { formatDateTime } from "../lib/format";
import { projectTitle } from "../lib/project-title";
import { talentDeclinesQuery } from "../lib/queries";
import "./searches.css";
import "./talent-declines.css";

/** Sort accessors are value EXTRACTORS (lib/table-sort.ts), so Project orders by the
 *  DERIVED title rather than the raw `projects.name`, which is routinely empty and would
 *  bunch every unnamed project at one end. Reason orders by its LABEL, so the column
 *  sorts the way it reads rather than by the underscored enum value. */
const DECLINE_SORT = {
  declined: (d: AdminDeclineRow) => d.declinedAt ?? null,
  talent: (d: AdminDeclineRow) => d.talentName ?? d.sellerId,
  project: (d: AdminDeclineRow) =>
    d.project.id
      ? projectTitle({ id: d.project.id, name: d.project.name, summary: d.project.summary })
      : null,
  reason: (d: AdminDeclineRow) => (d.declineReason ? declineReasonLabel(d.declineReason) : null),
  who: (d: AdminDeclineRow) => d.declinedBy,
} as const;

/** The two CLOSED vocabularies: both halves stay listed at zero, so "nobody
 *  declined us this week" reads as an answer rather than a missing control. */
const DECLINE_WHO_OPTIONS = [
  { value: "talent", label: "The talent" },
  { value: "us", label: "Us (out of scope)" },
] as const;

const DECLINE_REPLIED_OPTIONS = [
  { value: "yes", label: "Replied first" },
  { value: "no", label: "Never replied" },
] as const;

function DeclineRow({ decline }: { decline: AdminDeclineRow }) {
  const project = decline.project;
  return (
    <tr>
      <td>{formatDateTime(decline.declinedAt ?? null)}</td>
      <td>
        <ExplorerTalentLink sellerId={decline.sellerId} className="ex-primary-link">
          {decline.talentName ?? decline.sellerId}
        </ExplorerTalentLink>
        {decline.talentName ? <span className="rc-cell-sub">{decline.sellerId}</span> : null}
      </td>
      <td>
        {project.id ? (
          <ExplorerProjectLink projectId={project.id} className="ex-primary-link">
            {projectTitle({ id: project.id, name: project.name, summary: project.summary })}
          </ExplorerProjectLink>
        ) : (
          // The project row is gone (deleted account); the outreach record remains.
          <span className="tm-unknown">Project deleted</span>
        )}
        {project.owner ? <span className="rc-cell-sub">{project.owner}</span> : null}
      </td>
      <td>
        {decline.declineReason ? (
          declineReasonLabel(decline.declineReason)
        ) : (
          <span className="tm-unknown">Not recorded</span>
        )}
      </td>
      <td>
        {decline.declinedBy === "us" ? (
          <Badge
            tone="neutral"
            size="sm"
            title="We ended this one: JUNO judged the offer out of scope for the brief"
          >
            Us
          </Badge>
        ) : (
          <Badge tone="warning" size="sm" title="The talent turned this outreach down">
            Talent
          </Badge>
        )}
      </td>
      <td>
        {decline.declineDetail ? (
          // Their words, verbatim. `title` carries the full text so a long one is
          // readable without the column dictating the row height for every other row.
          <span className="td-detail" title={decline.declineDetail}>
            {decline.declineDetail}
          </span>
        ) : (
          <span className="tm-unknown">-</span>
        )}
      </td>
      <td>
        {project.id ? (
          <ExplorerProjectLink
            projectId={project.id}
            className="ex-row-link"
            ariaLabel="Open the project this decline belongs to"
            denied={null}
          >
            <ArrowRightIcon size={16} />
          </ExplorerProjectLink>
        ) : null}
      </td>
    </tr>
  );
}

export function TalentDeclinesPage() {
  const [reasons, setReasons] = useRestoredState<readonly string[]>("talent-declines/reasons", []);
  const [who, setWho] = useRestoredState<readonly string[]>("talent-declines/who", []);
  const [replied, setReplied] = useRestoredState<readonly string[]>("talent-declines/replied", []);
  // The cohort range. Local state like the filters beside it, and it narrows
  // SERVER-side for the same reason they do: this list is keyset-paged, so a
  // browser-side date filter could only ever cover the pages already fetched.
  const [range, setRange] = useRestoredState<DateRange>("talent-declines/range", EMPTY_DATE_RANGE);
  const declines = useInfiniteQuery(talentDeclinesQuery({ reasons, who, replied, range }));
  // Above the error branch: hooks cannot sit behind an early return.
  const loaded = useMemo<AdminDeclineRow[]>(
    () => declines.data?.pages.flatMap((p) => p.declines) ?? [],
    [declines.data],
  );
  /** Three repeating columns, and each is a question an operator actually asks
   *  of this list. Reason is the point of the page. Who separates their
   *  decisions from ours. Replied separates a considered no from a silent one.
   *
   *  Reason options come from the response's store-wide facet, so a new enum
   *  value cannot go missing; `hasNoValue` keeps the "No reason recorded"
   *  bucket selectable even while a reason filter hides those rows (the
   *  selection travels as the `NO_VALUE` sentinel, which the backend reads as
   *  "nothing recorded"). All three selections filter SERVER-side, across the
   *  whole lane. */
  const page0 = declines.data?.pages[0];
  const columns = useMemo<readonly FilterColumn<AdminDeclineRow>[]>(
    () => [
      {
        key: "reason",
        label: "Filter by reason",
        allLabel: "All reasons",
        value: (d) => d.declineReason,
        optionLabel: declineReasonLabel,
        options: facetOptions(page0?.reasons ?? [], declineReasonLabel, DECLINE_REASON_ORDER),
        hasNoValue: page0?.hasNoReason ?? false,
        // A thread can end declined with nothing recorded. That is a real
        // state, not a missing field, so it says so rather than being folded
        // into "Other".
        noValueLabel: "No reason recorded",
        selected: reasons,
        onChange: setReasons,
      },
      {
        key: "who",
        label: "Filter by who declined",
        allLabel: "Declined by: any",
        value: (d) => d.declinedBy,
        options: DECLINE_WHO_OPTIONS,
        selected: who,
        onChange: setWho,
      },
      {
        key: "replied",
        label: "Filter by engagement",
        allLabel: "Replied: any",
        value: (d) => (d.replied ? "yes" : "no"),
        options: DECLINE_REPLIED_OPTIONS,
        selected: replied,
        onChange: setReplied,
      },
    ],
    [page0, reasons, who, replied, setReasons, setWho, setReplied],
  );
  const filters = useColumnFilters(loaded, columns);
  const sorted = useTableSort(filters.rows, DECLINE_SORT, null, {
    restore: "talent-declines/sort",
  });

  if (declines.isError) {
    return (
      <div className="ad-page">
        <AreaBoundary
          area="talent_management"
          error={declines.error}
          onRetry={() => void declines.refetch()}
        />
      </div>
    );
  }

  return (
    <div className="ad-page">
      <header className="ad-page__head">
        <h1 className="ad-page__title">Declines</h1>
        <p className="ad-page__sub">
          Every outreach that ended in a no, newest first, with the reason and the talent's own
          words. Use the "Declined by" filter to separate their decisions from ours.
        </p>
      </header>

      <SectionTabs label="Talent Management sections" tabs={TALENT_TABS} />

      <div className="rc-toolbar sr-toolbar">
        <DateRangeFilter label="Declined" value={range} onChange={setRange} />
        <TableFilters
          controls={filters.controls}
          active={filters.active || isDateRangeActive(range)}
          onClear={() => {
            filters.clear();
            setRange(EMPTY_DATE_RANGE);
          }}
        />
        {/* Says "loaded", not "declines", for the same reason the Offers tab does: this
            list is keyset-paginated, so SORTING reaches the pages fetched so far and no
            further. The filters run server-side and cover the whole lane — every loaded
            row already matches them. Still no percentage anywhere on this page: a share
            of a partial page is a number that looks like a metric and is not one. */}
        {declines.data ? (
          <span className="rc-toolbar__count">
            {sorted.rows.length.toLocaleString()} of {loaded.length.toLocaleString()} loaded
            {declines.hasNextPage ? ", more available" : ""}
          </span>
        ) : null}
      </div>

      {declines.isPending ? (
        <TableSkeleton columns={7} />
      ) : loaded.length === 0 && !filters.active ? (
        <EmptyState
          title="No declines yet"
          message="When an outreach ends in a no, it will appear here with its reason."
        />
      ) : sorted.rows.length === 0 ? (
        <EmptyState
          title="No declines match"
          message="The whole lane was searched. Try clearing a filter."
        />
      ) : (
        <>
          <div className="rc-table-wrap">
            <table className="rc-table">
              <TableExport name="talent-declines" label="declines" />
              <thead>
                <tr>
                  <SortableTh
                    sortKey="declined"
                    ariaSort={sorted.ariaSort}
                    onToggle={sorted.toggle}
                  >
                    Declined
                  </SortableTh>
                  <SortableTh sortKey="talent" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Talent
                  </SortableTh>
                  <SortableTh sortKey="project" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Project
                  </SortableTh>
                  <SortableTh sortKey="reason" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Reason
                  </SortableTh>
                  <SortableTh sortKey="who" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Declined by
                  </SortableTh>
                  <th>Why</th>
                  <th>
                    <span className="ad-visually-hidden">Open project</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.rows.map((decline) => (
                  <DeclineRow key={decline.id} decline={decline} />
                ))}
              </tbody>
            </table>
          </div>
          {declines.hasNextPage ? (
            <div className="ex-load-more">
              <Button
                type="button"
                variant="outline"
                tone="neutral"
                size="sm"
                disabled={declines.isFetchingNextPage}
                onClick={() => void declines.fetchNextPage()}
              >
                {declines.isFetchingNextPage ? "Loading…" : "Load more declines"}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
